import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, FileSignature, Gavel, Send, Snowflake, Sparkles, Trophy } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { apiErr } from "../utils";
import { getStageProceedingRecipients } from "../api";
import type {
  StageProceeding,
  StageProceedingInput,
  StageRecipientCandidate,
} from "../types";

// Druga droga wyłonienia wykonawcy etapu (Projekt techniczny / Kosztorys / MDCP): zamiast
// wskazać firmę z ręki, wystawiamy etap w postępowaniu ofertowym. Zamówienie i tak powstaje
// w panelu etapu („Zleć zwycięzcy"), więc wszystkie automaty DLC_01 działają jak zawsze —
// tu jedynie prowadzimy przez stany: szkic → opublikowane → rozstrzygnięte → zlecone.
//
// Ten sam komponent obsługuje wszystkie trzy etapy; różnice są tylko w tekstach.

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const inDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return iso(d);
};
// Domyślny termin składania ofert: N dni ROBOCZYCH od dziś (sob./niedz. pomijane).
const inBusinessDays = (days: number) => {
  const d = new Date();
  let left = days;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
  }
  return iso(d);
};
const fmtDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("pl-PL") : "—";
const fmtDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "—";

const STATUS_STYLE: Record<string, string> = {
  draft: "bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]",
  published: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  awarded: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  cancelled: "bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]",
};

export interface StageProceedingPanelProps {
  proceeding: StageProceeding | null;
  /** Czy bieżący użytkownik prowadzi ten etap (kierownik przedsięwzięcia / dyrektor). */
  canManage: boolean;
  /** Etap, dla którego wystawiamy — steruje tylko tekstami formularza. */
  stage: "technical" | "cost_estimate" | "mdcp";
  starting: boolean;
  releasing: boolean;
  onStart: (payload: StageProceedingInput) => void;
  onRelease: () => void;
  /** Klik „Zleć zwycięzcy" — panel etapu otwiera swój formularz zlecenia w trybie zwycięzcy. */
  onOrderWinner: () => void;
  /** Ukryj przycisk zlecenia, gdy formularz zlecenia jest już otwarty. */
  orderFormOpen?: boolean;
  /** Id projektu ZE — włącza podpowiedź AI ceny maksymalnej (tylko MDCP). */
  connectionId?: string;
}

const HINTS: Record<string, string> = {
  technical: "Cały komplet dokumentacji projektowej trafia do wyceny rynkowej.",
  cost_estimate: "Kosztorys i dokumentacje powiązane trafiają do wyceny rynkowej.",
  mdcp: "Punkty foto i obrys zakresu zamrożą się w chwili publikacji ogłoszenia.",
};

interface PriceSuggestion {
  amount_net: string;
  contractor_name: string;
  distance_km: number | null;
  note: string;
  candidates_priced: number;
}

export function StageProceedingPanel({
  proceeding, canManage, stage, starting, releasing, onStart, onRelease, onOrderWinner,
  orderFormOpen, connectionId,
}: StageProceedingPanelProps) {
  const canRunRfq = !!useAuthStore((s) => s.user?.has_rfq_manage_permission);
  const [open, setOpen] = useState(false);
  // Domyślny wzór umowy dla tej ścieżki (ustawiany przez dyrekcję w RFQ → Wzory umów):
  // kupujący ma wiedzieć PRZED założeniem szkicu, czy ogłoszenie wyjdzie z wzorem.
  const templatesQ = useQuery({
    queryKey: ["pp", "stage-contract-templates"],
    queryFn: () =>
      apiClient
        .get<{ data: { templates: { path: string; file: { original_filename?: string } | null }[] } }>(
          "/power-projects/stage-contract-templates/",
        )
        .then((r) => r.data.data),
    enabled: open && !proceeding,
  });
  const contractTemplate = templatesQ.data?.templates.find((r) => r.path === stage)?.file ?? null;
  // Podpowiedź AI ceny maksymalnej (MDCP): najtańszy geodeta w promieniu 200 km, wyceniony
  // jego pozycjami z umowy ramowej. Podpowiedź budżetu, nie oferta — pole zostaje edytowalne,
  // a wpisana ręcznie kwota wygrywa z tym, co przyjdzie później.
  const priceAiQ = useQuery<PriceSuggestion>({
    queryKey: ["pp", "mdcp-suggest-price", connectionId],
    queryFn: () =>
      apiClient
        .post<{ data: PriceSuggestion }>(
          `/power-projects/connections/${connectionId}/mdcp/proceeding/suggest-price/`,
        )
        .then((r) => r.data.data),
    enabled: open && !proceeding && stage === "mdcp" && !!connectionId,
    staleTime: 10 * 60_000,
    retry: false,
  });
  // Domyślne terminy: oferty = 2 dni robocze (szybkie zapytanie, nie urzędowy przetarg),
  // realizacja = 45 dni od dziś. Oba edytowalne.
  const [submission, setSubmission] = useState(() => inBusinessDays(2));
  const [delivery, setDelivery] = useState(() => inDays(45));
  const [maxPrice, setMaxPrice] = useState("");
  const [priceVisible, setPriceVisible] = useState(false);
  const [attachTenderDocs, setAttachTenderDocs] = useState(false);
  const [attachRouteDxf, setAttachRouteDxf] = useState(true);
  // Zaproszenia mailem przy „opublikuj od razu": cały krąg (jak dotąd), wskazane firmy
  // albo nikt. Lista firm i wstępne zaznaczenie przychodzą z serwera dopiero po rozwinięciu
  // — to jedno wywołanie AI, więc nie odpalamy go każdemu, kto otworzy formularz.
  const [mailMode, setMailMode] = useState<"all" | "selected" | "none">("all");
  const [picked, setPicked] = useState<string[]>([]);
  const recipientsQ = useQuery({
    queryKey: ["pp", "stage-recipients", connectionId, stage],
    queryFn: () => getStageProceedingRecipients(connectionId!, stage),
    enabled: mailMode === "selected" && !!connectionId && !proceeding,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Podpowiedź zaznacza się sama — RAZ, przy pierwszym wyniku. Późniejsze odznaczenie
  // przez człowieka ma zostać odznaczone, a nie wracać przy każdym renderze.
  const [preselectApplied, setPreselectApplied] = useState(false);
  const candidates: StageRecipientCandidate[] = recipientsQ.data?.contractors ?? [];
  if (candidates.length > 0 && !preselectApplied) {
    setPreselectApplied(true);
    setPicked(candidates.filter((c) => c.preselected).map((c) => c.id));
  }

  const submit = (publish: boolean) =>
    onStart({
      submission_deadline: new Date(`${submission}T12:00:00`).toISOString(),
      delivery_deadline: delivery,
      max_price_net: maxPrice ? maxPrice : null,
      max_price_visible: priceVisible,
      attach_tender_docs: attachTenderDocs,
      attach_route_dxf: attachRouteDxf,
      publish,
      notify_by_mail: mailMode !== "none",
      mail_contractors: mailMode === "selected" ? picked : [],
    });

  // Nic nie wystawiono i nie ma czym wystawić — panel milczy (dokładnie jak dotąd).
  if (!proceeding && (!canManage || !canRunRfq)) return null;

  if (!proceeding) {
    if (!open) {
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-muted)] px-2.5 py-1.5 text-xs text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
        >
          <Gavel size={13} /> …albo wyłoń w postępowaniu ofertowym
        </button>
      );
    }
    // Jednokolumnowo i kompaktowo — formularz żyje też w wąskiej kolumnie panelu MDCP,
    // gdzie siatka dwukolumnowa gniecie pola dat (ten sam powód, co w DesignOrderForm).
    return (
      <div className="space-y-2.5 rounded-md border border-[var(--color-border-muted)] p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-[var(--color-text)]">
          <Gavel size={14} className="shrink-0 text-[var(--color-primary)]" />
          Wyłoń w postępowaniu ofertowym
        </div>
        <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">{HINTS[stage]}</p>
        <div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Termin składania ofert
          </div>
          <DatePicker value={submission} onChange={setSubmission} />
        </div>
        <div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Termin realizacji
          </div>
          <DatePicker value={delivery} onChange={setDelivery} />
        </div>
        <div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Cena maksymalna netto (opcjonalnie)
          </div>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input-base w-full text-xs"
            placeholder="np. 8000"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          />
          {priceAiQ.isFetching && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
              <Sparkles size={12} className="animate-pulse shrink-0 text-[var(--color-primary)]" />
              AI szuka najtańszego geodety w promieniu 200 km…
            </p>
          )}
          {priceAiQ.data && !priceAiQ.isFetching && (
            <p
              className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-[var(--color-text-secondary)]"
              title={priceAiQ.data.note || undefined}
            >
              <Sparkles size={12} className="mt-px shrink-0 text-[var(--color-primary)]" />
              <span>
                AI: najtaniej wychodzi{" "}
                <button
                  type="button"
                  className="font-semibold text-[var(--color-primary)] hover:underline"
                  title="Wstaw kwotę do pola"
                  onClick={() => setMaxPrice(priceAiQ.data.amount_net)}
                >
                  {Number(priceAiQ.data.amount_net).toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  zł
                </button>{" "}
                — {priceAiQ.data.contractor_name}
                {priceAiQ.data.distance_km != null && ` (~${Math.round(priceAiQ.data.distance_km)} km)`}
                , wg jego umowy ramowej
              </span>
            </p>
          )}
          {priceAiQ.isError && !priceAiQ.isFetching && (
            <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-[var(--color-text-muted)]">
              <Sparkles size={12} className="mt-px shrink-0" />
              {/* Konkretny powód z backendu („Brak geodetów w promieniu 200 km…"), nie ogólnik. */}
              <span>{apiErr(priceAiQ.error, "Podpowiedź AI niedostępna — wpisz kwotę ręcznie.")}</span>
            </p>
          )}
          <label className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--color-text)]">
            <input
              type="checkbox"
              className="shrink-0"
              checked={priceVisible}
              onChange={(e) => setPriceVisible(e.target.checked)}
            />
            Jawna dla oferentów (twardy limit ofert)
          </label>
        </div>
        {stage !== "mdcp" && (
          <div className="space-y-1.5 pt-0.5">
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
              <input
                type="checkbox"
                className="shrink-0"
                checked={attachRouteDxf}
                onChange={(e) => setAttachRouteDxf(e.target.checked)}
              />
              Dołącz aktualną trasę (DXF)
            </label>
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
              <input
                type="checkbox"
                className="shrink-0"
                checked={attachTenderDocs}
                onChange={(e) => setAttachTenderDocs(e.target.checked)}
              />
              Dołącz dokumentację przetargową ZE
            </label>
          </div>
        )}
        <p
          className="flex items-center gap-1.5 truncate text-[11px] text-[var(--color-text-muted)]"
          title={
            contractTemplate
              ? `Wzór umowy: ${contractTemplate.original_filename ?? "plik"} — dopnie się do ogłoszenia; można go zdjąć ze szkicu.`
              : "Brak domyślnego wzoru umowy — dyrekcja ustawia go w RFQ → Wzory umów."
          }
        >
          <FileSignature size={12} className="shrink-0" />
          <span className="truncate">
            {contractTemplate
              ? `Wzór umowy: ${contractTemplate.original_filename ?? "plik"}`
              : "Bez wzoru umowy (nie ustawiono)"}
          </span>
        </p>
        <div className="space-y-1.5 pt-0.5">
          <Button
            className="w-full"
            loading={starting}
            disabled={!submission || !delivery || starting}
            onClick={() => submit(false)}
          >
            <Gavel size={13} /> Utwórz szkic ogłoszenia
          </Button>
          {/* „Bez zatwierdzania": pomija etap szkicu — ogłoszenie od razu idzie do oferentów
              (te same walidacje i zawiadomienia, co przy publikacji z modułu Przetargi).
              Zaproszenie mailem to osobna decyzja od samego ogłoszenia: krąg widzących
              wyznacza tryb dostępu, a poniżej wybieramy tylko, do kogo pójdzie poczta. */}
          <div className="space-y-1.5 rounded-md bg-[var(--color-bg-muted)] p-2">
            <p className="text-[11px] font-semibold text-[var(--color-text)]">
              Zaproszenie mailem przy publikacji
            </p>
            {(
              [
                ["all", "Do wszystkich dopuszczonych firm"],
                ["selected", "Tylko do wskazanych (z podpowiedzią)"],
                ["none", "Bez maili — samo ogłoszenie"],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex items-center gap-1.5 text-xs text-[var(--color-text)]"
              >
                <input
                  type="radio"
                  className="shrink-0"
                  checked={mailMode === value}
                  onChange={() => setMailMode(value)}
                />
                {label}
              </label>
            ))}

            {mailMode === "selected" && (
              <div className="mt-1 space-y-1">
                {recipientsQ.isLoading && (
                  <p className="text-[11px] text-[var(--color-text-muted)]">
                    <Sparkles size={11} className="mr-1 inline" />
                    Dobieram firmy z historii współpracy…
                  </p>
                )}
                {recipientsQ.isError && (
                  <p className="text-[11px] text-[var(--color-danger)]">
                    Nie udało się pobrać kręgu firm — publikuj bez maili albo ze szkicu.
                  </p>
                )}
                {candidates.length === 0 && !recipientsQ.isLoading && !recipientsQ.isError && (
                  <p className="text-[11px] text-[var(--color-text-muted)]">
                    Żadna firma nie spełnia warunków tego ogłoszenia — nie ma kogo zaprosić.
                  </p>
                )}
                {candidates.length > 0 && (
                  <>
                    {/* Skąd wzięło się zaznaczenie — model czy punktacja z historii. Kupujący
                        ma wiedzieć, czemu ufa, zanim wyśle imienne zaproszenia. */}
                    <p className="text-[10px] text-[var(--color-text-muted)]">
                      {recipientsQ.data?.source === "ai"
                        ? "Wstępny wybór: AI na podstawie historii zleceń, ofert i ocen."
                        : "Wstępny wybór: punktacja z historii (AI niedostępne)."}
                      {recipientsQ.data?.note ? ` ${recipientsQ.data.note}` : ""}
                    </p>
                    <div className="max-h-44 space-y-0.5 overflow-y-auto">
                      {candidates.map((row) => (
                        <label
                          key={row.id}
                          className="flex items-start gap-1.5 rounded px-1 py-0.5 text-[11px] hover:bg-[var(--color-bg)]"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 shrink-0"
                            checked={picked.includes(row.id)}
                            onChange={() =>
                              setPicked((prev) =>
                                prev.includes(row.id)
                                  ? prev.filter((x) => x !== row.id)
                                  : [...prev, row.id],
                              )
                            }
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-[var(--color-text)]">
                              {row.name}
                            </span>
                            <span className="block text-[10px] text-[var(--color-text-muted)]">
                              {row.reason ||
                                [
                                  row.orders ? `${row.orders} zleceń` : "",
                                  row.offers ? `${row.offers} ofert` : "",
                                  row.rating !== null ? `ocena ${row.rating.toFixed(1)}` : "",
                                  row.distance_km !== null
                                    ? `${row.distance_km.toFixed(0)} km`
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || "brak historii u nas"}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-[var(--color-text-muted)]">
                      Zaznaczono {picked.length} z {candidates.length}. Pozostałe firmy i tak
                      zobaczą ogłoszenie w systemie — nie dostaną tylko maila.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            className="w-full"
            loading={starting}
            disabled={
              !submission ||
              !delivery ||
              starting ||
              (mailMode === "selected" && picked.length === 0)
            }
            onClick={() => {
              const question =
                mailMode === "all"
                  ? "Opublikować od razu, bez szkicu? Ogłoszenie natychmiast trafi do kręgu " +
                    "oferentów (dzwonek + mail) i nie będzie już edytowalne."
                  : mailMode === "selected"
                    ? `Opublikować od razu i wysłać zaproszenie do ${picked.length} wskazanych ` +
                      "firm? Pozostałe zobaczą ogłoszenie w systemie, ale bez maila. " +
                      "Treści nie będzie już można zmienić."
                    : "Opublikować od razu, bez szkicu i BEZ maili? Ogłoszenie zobaczą firmy " +
                      "w systemie (dzwonek), ale nikt nie dostanie zaproszenia pocztą. " +
                      "Treści nie będzie już można zmienić.";
              if (window.confirm(question)) submit(true);
            }}
          >
            <Send size={13} /> Utwórz i opublikuj od razu
          </Button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="block w-full rounded-md border border-[var(--color-border-muted)] px-2.5 py-1.5 text-center text-xs font-medium text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text)]"
          >
            Anuluj
          </button>
        </div>
      </div>
    );
  }

  const ordered = !!proceeding.order_id;
  const badge = STATUS_STYLE[proceeding.status] ?? STATUS_STYLE.draft;
  return (
    <div
      className={`space-y-2 rounded-xl border p-3 ${
        proceeding.awaiting_order
          ? "border-emerald-300 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/15"
          : "border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Gavel size={13} className="shrink-0 text-[var(--color-primary)]" />
        <Link
          to={`/rfq/proceedings/${proceeding.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-primary)] hover:underline"
        >
          {proceeding.number} <ExternalLink size={11} />
        </Link>
        <span className={`rounded px-1.5 py-px text-[10px] font-semibold uppercase ${badge}`}>
          {proceeding.status_display}
        </span>
        {proceeding.link_kind === "correction" && (
          <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            Korekcyjne
          </span>
        )}
        {proceeding.frozen_at && (
          <span
            className="inline-flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]"
            title={`Zakres zamrożony przy publikacji ${fmtDateTime(proceeding.frozen_at)}`}
          >
            <Snowflake size={11} /> zakres zamrożony
            {proceeding.points_frozen > 0 && ` · ${proceeding.points_frozen} pkt`}
            {proceeding.files_frozen > 0 && ` · ${proceeding.files_frozen} plik(ów)`}
          </span>
        )}
      </div>

      <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">
        {proceeding.title} · oferty do {fmtDateTime(proceeding.submission_deadline)} · realizacja do{" "}
        {fmtDate(proceeding.delivery_deadline)}
      </p>

      {proceeding.winner && (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-text)]">
          <Trophy size={13} className="shrink-0 text-emerald-600" />
          <span className="font-semibold">{proceeding.winner.contractor_name}</span>
          <span className="font-mono text-[var(--color-text-muted)]">
            {Number(proceeding.winner.amount_net).toLocaleString("pl-PL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            zł netto
          </span>
        </p>
      )}

      {ordered ? (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Zlecono zwycięzcy — zamówienie widoczne w zamówieniach etapu powyżej.
        </p>
      ) : (
        canManage && (
          <div className="flex flex-wrap items-center gap-2">
            {proceeding.awaiting_order && !orderFormOpen && (
              <Button loading={false} onClick={onOrderWinner}>
                <Trophy size={13} /> Zleć zwycięzcy
              </Button>
            )}
            <button
              type="button"
              disabled={releasing}
              onClick={() => {
                if (
                  window.confirm(
                    "Odstąpić od postępowania? Etap wróci do zwykłego zlecania, a punkty MDCP " +
                      "zostaną odmrożone. Samo ogłoszenie zostaje w module Przetargi — jeśli wciąż " +
                      "trwa, zamknij je tam osobno.",
                  )
                ) {
                  onRelease();
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-muted)] px-2.5 py-1.5 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)]"
            >
              <AlertTriangle size={12} /> Odstąp od postępowania
            </button>
          </div>
        )
      )}

      {!ordered && !proceeding.awaiting_order && (
        <p className="text-[10px] leading-snug text-[var(--color-text-muted)]">
          Dopóki postępowanie trwa, zlecenie wprost na tym etapie jest zablokowane.
        </p>
      )}
    </div>
  );
}
